// spec 072: incluye el global `return` (Devolver) además de open/openSizeChart.
/**
 * Ultra-small entry script. This is the file the merchant pastes via
 *
 *   <script async src="https://cdn.pilcha.ai/widget.js" data-store-token="..."></script>
 *
 * Optional attributes: `data-locale="pt-BR"` (spec 024) pins the widget's
 * language above the shopper's browser; omit it to auto-detect (store language
 * → navigator → es-AR).
 *
 * Responsibilities (and NOTHING else):
 *   1. Detect whether the current page is a PDP.
 *   2. Find an injection point near the size selector.
 *   3. Drop a "Probarme esto" button in the DOM.
 *   4. On click, lazy-load the real app (main.js) and open the modal.
 *
 * Everything heavy (Solid, forms, upload, AI polling) lives in main.tsx
 * so this file stays tiny (<3kb gzipped) and never blocks render.
 */

import { detectProductId } from './detect-product-id.js';
import { hasNubeSdkEmbed } from './nubesdk-embed.js';
// spec 120: the try-on button (style helpers, orb layout, default texts) lives in
// tryon-button.ts so the NubeSDK iframe (embed.ts) draws the very same button.
import {
  BUTTON_SUBTEXT,
  borderCss,
  customLabelFor,
  ensureFont,
  fontFamilyCss,
  fontWeightCss,
  layoutOrbButton,
  radiusCss,
  setButtonContent,
  styleBlockButton,
  textColorCss,
  tryonColor,
  tryonLabelFor,
  typographyExtrasCss,
  wantsOrb,
} from './tryon-button.js';
import type { ButtonStyle, WidgetLocale } from './tryon-button.js';

// Force-export so `declare global` below is treated as a module augmentation
// instead of a script-level redeclaration. The empty export has no runtime
// cost. Vite's library build elides it.
export {};

interface PilchaGlobals {
  version: string;
  open(productId?: string): void;
  // spec 062: open the standalone size-guide modal for a product.
  openSizeChart(productId?: string): void;
  // spec 072: open the standalone return-reason capture for a product.
  return(productId?: string): void;
  close(): void;
  _loaded?: boolean;
  _config?: {
    storeToken: string;
    cdnUrl: string;
    apiBaseUrl: string;
    // spec 024: merchant-forced locale via `data-locale` script attribute (or
    // `?locale=`). Highest priority — a Brazilian store can pin `pt-BR` no matter
    // the shopper's browser. Unset → falls back to store language / navigator.
    locale?: string;
    // Optional per-store overrides the merchant configures (returned by
    // /storefront/lookup). When unset the button uses a localized default.
    // buttonText is a per-locale map; the loader picks the store's language.
    buttonText?: Partial<Record<'es' | 'en' | 'pt', string>>;
    buttonColor?: string;
    // spec 051/070: extra button style (font/weight/tracking/radius/border/icon).
    buttonStyle?: ButtonStyle;
    // spec 070: the size-guide button's OWN, independent customization. When
    // `sizeGuideButtonColor` is set the button renders filled (like try-on);
    // unset → the compact underlined text link (back-compat).
    sizeGuideButtonText?: Partial<Record<'es' | 'en' | 'pt', string>>;
    sizeGuideButtonColor?: string;
    sizeGuideButtonStyle?: ButtonStyle;
    // spec 060: optional Google login for the shopper account step. `popupUrl`
    // is the Pilcha-hosted GIS page (auth.pilcha.ai/google-widget). Set via the
    // `data-google-popup-url` script attribute; absent → the CTA stays hidden.
    googleLogin?: { enabled: boolean; popupUrl: string };
  };
  _handlers?: Map<string, Set<(payload?: unknown) => void>>;
}

declare global {
  interface Window {
    Pilcha?: PilchaGlobals;
  }
}

// Production fallbacks. Used when the script is hosted by a third party
// (e.g. Tiendanube's Scripts API serves the loader from their own CDN, so
// deriving cdnUrl/apiBaseUrl from `script.src` would point to Tiendanube
// instead of us). Always pinned to the prod hosts; dev/staging overrides
// happen via the data-api-base / ?apiBase= attributes that the existing
// integrations already use.
//
// Infra migration (2026-08): compute moved off the departing dev's AWS to
// Railway. The pilcha.ai apex/subdomains still resolve to the old AWS boxes,
// so until the api./cdn. DNS is cut over to Railway these fallbacks must be
// the Railway hosts directly — otherwise a Tiendanube-served loader hits the
// old infra (which lacks the new stores) and the widget silently disables.
const PILCHA_PROD_API_BASE = 'https://pilcha-production.up.railway.app';
const PILCHA_PROD_CDN = 'https://widget-production-f08b.up.railway.app';
// spec 060: hosted Google sign-in popup (fixed origin, whitelisted in Google).
// Served by the landing. Overridable per-store via `data-google-popup-url`.
const PILCHA_PROD_POPUP_URL = 'https://pilcha.ai/google-widget';

// De-dupe if the tag is included twice.
if (!window.Pilcha?._loaded) {
  // `document.currentScript` is null for module scripts (Vite `format: 'es'`
  // emits an ESM bundle). Fall back to the first `<script>` tag carrying our
  // public `data-store-token` attribute. The query-string form is supported
  // too, for platform integrations that can't easily set data-attrs (e.g.
  // Tiendanube Scripts API).
  const script =
    (document.currentScript as HTMLScriptElement | null) ??
    (document.querySelector('script[data-store-token]') as HTMLScriptElement | null) ??
    (document.querySelector('script[src*="loader"]') as HTMLScriptElement | null);
  const scriptUrl = parseScriptUrl(script?.src ?? '');
  const explicitToken =
    script?.dataset.storeToken ?? scriptUrl?.searchParams.get('storeToken') ?? '';
  // Strip the script's own filename to get the base URL the bundle was
  // served from. Works for /widget.js, /loader.js, /widget/loader.js, etc.
  // For Tiendanube the loader is served by Tiendanube's CDN, so this gives
  // a non-Pilcha URL → fall through to prod constants below.
  const derivedCdnUrl = (script?.src ?? '').replace(/\/[^/]+\.[tj]s(\?[^#]*)?$/, '');
  const scriptHost = parseScriptUrl(script?.src ?? '')?.hostname ?? '';
  const isPilchaCdn = /(^|\.)pilcha\.ai$/i.test(scriptHost);
  // Local dev: when the loader is served from localhost (the widget's Vite dev
  // server), load `main.js` from that same origin instead of the prod CDN, so
  // `pnpm dev` tests the LOCAL widget bundle. (Vite dev aliases /main.js →
  // src/main.tsx — see vite.config.ts.) Prod/Tiendanube paths are unchanged.
  const isLocalDev = scriptHost === 'localhost' || scriptHost === '127.0.0.1';
  // Railway widget hosts (widget-staging-*/widget-production-*.up.railway.app): load main.js
  // from the SAME service that served the loader. Without this the staging loader pulled the
  // PRODUCTION main.js, so staging never showed its own widget code (seen 27/10 testing
  // Talles v2). In production the result is unchanged (own origin == PILCHA_PROD_CDN).
  const isRailwayWidget = /^widget-[a-z0-9-]+\.up\.railway\.app$/i.test(scriptHost);
  const useOwnOrigin = isPilchaCdn || isLocalDev;
  // Only the bundle origin follows the Railway host; the API base keeps its previous rules
  // (data-api-base, else the prod API) — deriving it from a railway widget host would be wrong.
  const cdnUrl = useOwnOrigin || isRailwayWidget ? derivedCdnUrl : PILCHA_PROD_CDN;
  const apiBaseUrl =
    script?.dataset.apiBase ??
    scriptUrl?.searchParams.get('apiBase') ??
    (useOwnOrigin ? deriveApiBaseUrl(derivedCdnUrl) : PILCHA_PROD_API_BASE);

  // spec 060: Google login on the shopper account step. On by default, pointing
  // at the hosted popup; a store can override the URL via `data-google-popup-url`.
  // The real gate is the backend (GOOGLE_LOGIN_ENABLED + SHOPPER_ACCOUNTS_ENABLED),
  // same "show the UI, backend gates it" contract as the rest of the account step.
  const googlePopupUrl =
    script?.dataset.googlePopupUrl ?? scriptUrl?.searchParams.get('googlePopupUrl') ?? PILCHA_PROD_POPUP_URL;
  const googleLogin = { enabled: true, popupUrl: googlePopupUrl };

  // spec 024: optional merchant-forced locale. `data-locale="pt-BR"` (or
  // `?locale=pt-BR`) pins the widget's language above browser detection.
  const locale =
    script?.dataset.locale ?? scriptUrl?.searchParams.get('locale') ?? undefined;

  if (explicitToken) {
    installLoader({ storeToken: explicitToken, cdnUrl, apiBaseUrl, googleLogin, locale });
  } else {
    // No explicit storeToken. Check if we're on a known platform storefront
    // (Tiendanube exposes window.LS.store.id) and resolve the token via the
    // public lookup endpoint. Fire-and-forget — failure logs and disables.
    void resolveStoreTokenFromPlatform({ cdnUrl, apiBaseUrl, googleLogin, locale });
  }
}

interface BootstrapContext {
  cdnUrl: string;
  apiBaseUrl: string;
  // spec 060: forwarded so the platform-resolved path also gets the Google CTA.
  googleLogin?: { enabled: boolean; popupUrl: string };
  // spec 024: forwarded so a `data-locale` override survives platform resolution.
  locale?: string;
}

async function resolveStoreTokenFromPlatform(ctx: BootstrapContext): Promise<void> {
  const w = window as unknown as { LS?: { store?: { id?: string | number } } };
  const tnStoreId = w.LS?.store?.id;
  if (tnStoreId != null) {
    try {
      const url = `${ctx.apiBaseUrl}/api/v1/storefront/lookup?platform=tiendanube&platformStoreId=${encodeURIComponent(String(tnStoreId))}`;
      const res = await fetch(url, { credentials: 'omit' });
      if (!res.ok) {
        console.warn(`[pilcha] storefront lookup failed http_${res.status}; widget disabled`);
        return;
      }
      const json = (await res.json()) as {
        storeToken?: string;
        widgetEnabled?: boolean;
        buttonText?: Partial<Record<'es' | 'en' | 'pt', string>> | null;
        buttonColor?: string | null;
        buttonStyle?: ButtonStyle | null;
        sizeGuideButtonText?: Partial<Record<'es' | 'en' | 'pt', string>> | null;
        sizeGuideButtonColor?: string | null;
        sizeGuideButtonStyle?: ButtonStyle | null;
      };
      if (!json.storeToken || json.widgetEnabled === false) {
        console.warn('[pilcha] storefront lookup returned no token; widget disabled');
        return;
      }
      installLoader({
        storeToken: json.storeToken,
        cdnUrl: ctx.cdnUrl,
        apiBaseUrl: ctx.apiBaseUrl,
        buttonText: json.buttonText ?? undefined,
        buttonColor: json.buttonColor ?? undefined,
        buttonStyle: json.buttonStyle ?? undefined,
        sizeGuideButtonText: json.sizeGuideButtonText ?? undefined,
        sizeGuideButtonColor: json.sizeGuideButtonColor ?? undefined,
        sizeGuideButtonStyle: json.sizeGuideButtonStyle ?? undefined,
        googleLogin: ctx.googleLogin,
        locale: ctx.locale,
      });
      return;
    } catch (err) {
      console.warn(`[pilcha] storefront lookup error: ${String(err)}; widget disabled`);
      return;
    }
  }

  console.warn('[pilcha] no storeToken and no recognized platform context; widget disabled');
}

function parseScriptUrl(src: string): URL | null {
  if (!src) return null;
  try {
    return new URL(src, window.location.href);
  } catch {
    return null;
  }
}

function installLoader(config: NonNullable<PilchaGlobals['_config']>): void {
  const handlers = new Map<string, Set<(p?: unknown) => void>>();

  const api: PilchaGlobals = {
    version: '0.1.0',
    _loaded: true,
    _config: config,
    _handlers: handlers,
    open(productId?: string) {
      loadMainAndCall('open', productId);
    },
    openSizeChart(productId?: string) {
      loadMainAndCall('openSizeChart', productId);
    },
    return(productId?: string) {
      loadMainAndCall('return', productId);
    },
    close() {
      loadMainAndCall('close');
    },
  };

  // expose the API
  Object.defineProperty(window, 'Pilcha', { value: api, writable: false });

  // spec 066: warm the TLS/DNS to the API + CDN as early as possible, so the
  // PDP `/products/lookup` and the eventual `main.js` import don't each pay a
  // fresh connection. Cheap, best-effort, and harmless if the hosts differ.
  addPreconnect(config.apiBaseUrl);
  addPreconnect(config.cdnUrl);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleInject, { once: true });
  } else {
    scheduleInject();
  }
}

/** spec 066: best-effort preconnect to an origin (dedup by href). */
function addPreconnect(url: string | undefined): void {
  if (!url) return;
  let origin: string;
  try {
    origin = new URL(url, window.location.href).origin;
  } catch {
    return;
  }
  if (document.querySelector(`link[rel="preconnect"][href="${origin}"]`)) return;
  const link = document.createElement('link');
  link.rel = 'preconnect';
  link.href = origin;
  link.crossOrigin = 'anonymous';
  document.head.appendChild(link);
}

/**
 * spec 066: keyframes for the button's loading spinner. Injected once into the
 * storefront <head> (the button lives in the light DOM, not the widget shadow
 * root, so it needs a real stylesheet for the animation).
 */
function ensureSpinnerStyle(): void {
  if (document.getElementById('pilcha-spinner-style')) return;
  const style = document.createElement('style');
  style.id = 'pilcha-spinner-style';
  style.textContent =
    '@keyframes pilcha-spin{to{transform:rotate(360deg)}}' +
    '.pilcha-spinner{display:inline-block;width:16px;height:16px;border:2px solid currentColor;' +
    'border-top-color:transparent;border-radius:50%;animation:pilcha-spin .6s linear infinite;opacity:.7}';
  document.head.appendChild(style);
}

function scheduleInject(): void {
  // Defer one tick so client-side hydration (Next.js, VTEX IO React, etc.)
  // gets a chance to render its own try-on button if it has one. Then the
  // `[data-pilcha-tryon-button]` self-check kicks in and we skip injection.
  // 200ms is a safe heuristic. first paint is usually under 100ms post-DCL,
  // hydration under 200ms for typical PDPs.
  window.setTimeout(tryInjectButton, 200);
}

// ─── Button locale + merchant overrides ────────────────────────────────────


/**
 * Locale for the injected button. Uses the STORE's rendered language (not the
 * shopper's browser), so a Brazilian store shows Portuguese even if the
 * shopper's browser is in Spanish. Tiendanube renders `<html lang="pt">` and
 * may expose `window.LS.store.language`.
 */
function detectLocale(): WidgetLocale {
  const w = window as unknown as { LS?: { store?: { language?: string } } };
  const raw = (
    // spec 024: merchant-forced `data-locale` wins over the store/browser.
    window.Pilcha?._config?.locale ||
    w.LS?.store?.language ||
    document.documentElement.lang ||
    navigator.language ||
    'es'
  ).toLowerCase();
  if (raw.startsWith('pt')) return 'pt';
  if (raw.startsWith('en')) return 'en';
  return 'es';
}

/** True when the merchant set their own try-on text for the store's language. */
function hasCustomLabel(): boolean {
  return !!customLabelFor(window.Pilcha?._config?.buttonText, detectLocale());
}

/** Merchant-set text for the store's language wins; else localized default. */
function buttonLabel(): string {
  return tryonLabelFor(window.Pilcha?._config?.buttonText, detectLocale());
}

// spec 062: the size-guide link label. Kept in the loader (mirrors BUTTON_TEXT)
// so the tiny loader doesn't pull in the full i18n table before main.js loads.
const SIZE_CHART_BUTTON_TEXT: Record<WidgetLocale, string> = {
  es: 'Tabla de talles',
  pt: 'Tabela de medidas',
  en: 'Size guide',
};
/** Size-guide button label: merchant override (per-locale) wins; else default. */
function sizeChartLabel(): string {
  const locale = detectLocale();
  const custom = window.Pilcha?._config?.sizeGuideButtonText?.[locale];
  return custom && custom.trim() ? custom.trim() : SIZE_CHART_BUTTON_TEXT[locale];
}

/** Try-on button colour: merchant override wins; else Pilcha black (spec 120 variant A). */
function buttonColor(): string {
  return tryonColor(window.Pilcha?._config?.buttonColor);
}

interface ProductState {
  /** spec 047/057: try-on OR (sizing + data) is on → inject the try-on CTA. */
  activated: boolean;
  /** spec 062: a body size chart is resolvable → inject the "Tabla de talles" link. */
  sizeChartAvailable: boolean;
}

const NO_PRODUCT_STATE: ProductState = { activated: false, sizeChartAvailable: false };

/**
 * spec 047/062: one public lookup per PDP, read once for both injected controls.
 * - `activated` gates the try-on button (merchant opt-in: try-on, or sizing WITH
 *   data — spec 057).
 * - `sizeChartAvailable` gates the size-guide button (a resolvable body chart).
 * Conservative on failure: a miss/404/network error → nothing injected, so a
 * disabled product never shows a control that would dead-end on a 403.
 */
async function fetchProductState(productId: string): Promise<ProductState> {
  const cfg = window.Pilcha?._config;
  if (!cfg?.apiBaseUrl || !cfg.storeToken) return NO_PRODUCT_STATE;

  // spec 066: cache the per-PDP lookup for this tab so revisits (and the SPA
  // re-navigating between products) don't re-round-trip. Keyed by store+product.
  const cacheKey = `pilcha:lookup:${cfg.storeToken.slice(-8)}:${productId}`;
  const cached = readCachedState(cacheKey);
  if (cached) return cached;

  try {
    const url = `${cfg.apiBaseUrl}/api/v1/products/lookup?vtexProductId=${encodeURIComponent(productId)}`;
    const res = await fetch(url, {
      credentials: 'omit',
      headers: { 'x-pilcha-store-token': cfg.storeToken },
    });
    if (!res.ok) return NO_PRODUCT_STATE;
    const json = (await res.json()) as {
      tryonEnabled?: boolean;
      sizingEnabled?: boolean;
      sizeDataAvailable?: boolean;
      sizeChartAvailable?: boolean;
    };
    // spec 057: the size feature counts as "on" only when the merchant enabled it
    // AND the product has sizing data (a chart / garment measurements). Without
    // data there's nothing to recommend, so a sizing-only product with no data (and
    // try-on off) shows no button at all.
    const sizingAvailable = json.sizingEnabled === true && json.sizeDataAvailable === true;
    const state: ProductState = {
      activated: json.tryonEnabled === true || sizingAvailable,
      sizeChartAvailable: json.sizeChartAvailable === true,
    };
    writeCachedState(cacheKey, state);
    return state;
  } catch {
    return NO_PRODUCT_STATE;
  }
}

const LOOKUP_CACHE_TTL_MS = 5 * 60 * 1000; // 5 min: fresh enough, avoids re-fetch on revisits.

function readCachedState(key: string): ProductState | null {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { t: number; s: ProductState };
    if (!parsed || Date.now() - parsed.t > LOOKUP_CACHE_TTL_MS) return null;
    return parsed.s;
  } catch {
    return null;
  }
}

function writeCachedState(key: string, state: ProductState): void {
  try {
    sessionStorage.setItem(key, JSON.stringify({ t: Date.now(), s: state }));
  } catch {
    /* private mode / quota / disabled — cache is best-effort */
  }
}

/**
 * spec 076 (card "métricas que no cierran"): record a widget IMPRESSION — the
 * try-on button was actually shown to the shopper on this PDP. This is the CTR
 * denominator (opens ÷ impressions). It's emitted HERE, from the loader (once per
 * PDP load), and NOT from main.tsx: that bundle only loads on the first modal
 * open, so it undercounted impressions and CTR came out >100%. Best-effort beacon
 * — it must never block or throw; analytics can't affect the storefront.
 */
let impressionSent = false;
function trackImpression(productId: string): void {
  // The loader runs once per page load, but guard anyway so a double pass can't
  // double-count a single PDP view.
  if (impressionSent) return;
  const cfg = window.Pilcha?._config;
  if (!cfg?.apiBaseUrl || !cfg.storeToken) return;
  impressionSent = true;
  try {
    const body = JSON.stringify({
      storeToken: cfg.storeToken,
      events: [
        {
          type: 'widget.loaded',
          sessionId: null,
          timestamp: new Date().toISOString(),
          payload: { productId },
        },
      ],
    });
    const url = `${cfg.apiBaseUrl}/api/v1/events`;
    const blob = new Blob([body], { type: 'application/json' });
    if (!navigator.sendBeacon?.(url, blob)) {
      void fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
      }).catch(() => {});
    }
  } catch {
    /* best-effort: analytics never breaks the storefront */
  }
}

async function tryInjectButton(): Promise<void> {
  // Tiendanube: only ever inject on a real product page. On the home/listing,
  // product cards expose `[data-product-id]`, which the generic detection
  // below would otherwise grab — dropping the button into the catalog and
  // breaking its layout. Tiendanube tags the <body> `template-product` only
  // on a PDP.
  const ls = (window as unknown as { LS?: { product?: { id?: unknown } } }).LS;
  if (ls != null) {
    // Real PDP signals: the `template-product` <body> class (Tiendanube
    // convention) or a populated `LS.product`. Require at least one; on the
    // home/listing neither holds, so we bail before the generic detection
    // grabs a catalog card's [data-product-id].
    const isPdp =
      document.body.classList.contains('template-product') || ls.product?.id != null;
    if (!isPdp) return;
  }

  const productId = detectProductId();
  if (!productId) return;

  // Self-collision guards: a merchant template may provide its own controls
  // (marked with the data-attrs), and another async pass may have injected ours.
  const hasTryonButton = () => document.querySelector('[data-pilcha-tryon-button]') != null;
  const hasSizeChartButton = () =>
    document.querySelector('[data-pilcha-size-chart-button]') != null;
  if (hasTryonButton() && hasSizeChartButton()) return;
  // Tiendanube runs two Pilcha scripts side by side (classic + NubeSDK). On a
  // theme where the NubeSDK one also renders, its iframe already carries the
  // try-on button: don't add a second one (the size guide stays ours).
  const nubeSdkHasTryon = hasNubeSdkEmbed();

  // Shopify discriminator: `window.ShopifyAnalytics.meta.product` is injected
  // only on Shopify PDPs (the same signal detectProductId uses on line ~210).
  // Tiendanube exposes `window.LS` instead and never sets ShopifyAnalytics, so
  // gating the Shopify-only anchors behind this guarantees Tiendanube/VTEX/etc.
  // behave EXACTLY as before — those selectors can never run off-Shopify.
  const isShopify =
    (window as unknown as { ShopifyAnalytics?: { meta?: { product?: unknown } } })
      .ShopifyAnalytics?.meta?.product != null;

  // WooCommerce discriminator: `body.single-product` + the classic add-to-cart
  // form. Gated the same way as the Shopify anchors so VTEX/Tiendanube/Shopify
  // paths are byte-for-byte unchanged — these selectors can only run on a Woo PDP.
  const isWoo = document.body.classList.contains('single-product');

  // Tiendanube discriminator: exposes `window.LS`. Its themes use `.js-addtocart`
  // / `.btn-add-to-cart` for the buy button and `.js-product-form` / `#product_form`
  // for the form. Gated by `window.LS` so it only runs on a Tiendanube storefront.
  const isTiendanube =
    (window as unknown as { LS?: { product?: unknown } }).LS?.product != null;

  // Shopify (Dawn, Horizon + most themes): anchor right by the add-to-cart
  // button. `button[name="add"]` is Dawn's submit; newer themes (Horizon) wrap
  // it in `<product-form>` / a `form[action*="/cart/add"]` whose submit isn't
  // named `add`. Fall back to the form itself.
  const shopifyAnchor = isShopify
    ? document.querySelector('button[name="add"]') ??
      document.querySelector(
        'form[action*="/cart/add"] button[type="submit"], form[action*="/cart/add"] [type="submit"]',
      ) ??
      document.querySelector('product-form button, product-form [type="submit"]') ??
      document.querySelector('form[action*="/cart/add"], product-form')
    : null;
  // WooCommerce: the single add-to-cart button, then the cart form.
  const wooAnchor = isWoo
    ? document.querySelector('.single_add_to_cart_button') ??
      document.querySelector('form.cart')
    : null;
  // Tiendanube: the add-to-cart button, then the product form.
  const tiendanubeAnchor = isTiendanube
    ? document.querySelector('.btn-add-to-cart, .js-addtocart') ??
      document.querySelector('.js-product-form, #product_form')
    : null;

  // Platform-specific anchors WIN over the generic selectors — otherwise a
  // stray high-up `[data-product-id]` (e.g. a Horizon section wrapper) grabs the
  // CTA and it lands at the top of the page instead of beside the buy button.
  const anchor =
    shopifyAnchor ??
    wooAnchor ??
    tiendanubeAnchor ??
    // Generic fallbacks (VTEX + themes that expose no platform global).
    document.querySelector('.sku-selector, [data-testid="sku-selector"]') ??
    document.querySelector('[data-product-id]') ??
    document.querySelector('form[name="checkout"]');
  if (!anchor?.parentElement) return;

  // spec 070: preload the Google Fonts for BOTH buttons (best-effort).
  ensureFont(window.Pilcha?._config?.buttonStyle);
  ensureFont(window.Pilcha?._config?.sizeGuideButtonStyle);

  // spec 066: OPTIMISTIC render. Inject the try-on button IMMEDIATELY in a
  // loading state (spinner, disabled) so it appears without waiting on the API
  // round-trip and reserves its final space (no layout shift). After the lookup
  // resolves we either reveal it (activated) or remove it (not activated). The
  // shopper sees a spinner over the button until we confirm — never a late jump.
  const canTryon = !hasTryonButton() && !nubeSdkHasTryon;
  let tryonBtn: HTMLButtonElement | null = null;
  if (canTryon) {
    tryonBtn = document.createElement('button');
    tryonBtn.type = 'button';
    tryonBtn.setAttribute('data-pilcha-tryon-button', '');
    tryonBtn.setAttribute('data-product-id', productId);
    tryonBtn.setAttribute('data-pilcha-loading', '');
    styleTryonButton(tryonBtn, isShopify, isTiendanube);
    tryonBtn.disabled = true;
    tryonBtn.style.cursor = 'default';
    ensureSpinnerStyle();
    const spinner = document.createElement('span');
    spinner.className = 'pilcha-spinner';
    tryonBtn.appendChild(spinner);
    // Insert right after the buy button so it sits directly beneath it, aligned.
    anchor.parentElement.insertBefore(tryonBtn, anchor.nextSibling);
  }

  // One lookup per PDP (cached, spec 066), read once for both controls. The
  // modal's own lookup on click still runs (cheap, revalidates).
  const productState = await fetchProductState(productId);

  // Finalize the try-on button: reveal it (activated) or remove it (not).
  if (tryonBtn) {
    if (productState.activated) {
      tryonBtn.removeAttribute('data-pilcha-loading');
      // spec 070: label + optional inline icon.
      const tryonStyle = window.Pilcha?._config?.buttonStyle;
      setButtonContent(tryonBtn, buttonLabel(), tryonStyle);
      // spec 120: variant A layout — orb | label + subline | arrow.
      if (wantsOrb(tryonStyle)) {
        layoutOrbButton(tryonBtn, tryonStyle, {
          label: buttonLabel(),
          subline: hasCustomLabel() ? null : BUTTON_SUBTEXT[detectLocale()],
          color: buttonColor(),
        });
      }
      tryonBtn.disabled = false;
      tryonBtn.style.cursor = 'pointer';
      // spec 076: the button is now visible to the shopper → count the impression
      // (CTR denominator). Only when activated, so we never count a PDP where the
      // try-on button wasn't actually offered.
      trackImpression(productId);
      tryonBtn.addEventListener('click', (ev) => {
        ev.preventDefault();
        window.Pilcha?.open(productId);
      });
      // The NubeSDK iframe can mount AFTER us (its worker loads on its own
      // schedule): if it shows up, step aside so the shopper sees one button.
      removeWhenNubeSdkEmbedAppears(tryonBtn);
    } else {
      tryonBtn.remove();
      tryonBtn = null;
    }
  }

  // spec 062/070: the size-guide button. Has its OWN, independent customization.
  // - Default (no colour set): the compact underlined text link (back-compat).
  // - Colour set: a filled button, styled from `sizeGuideButtonStyle`, like try-on.
  // Both honor the merchant's icon/font/weight/tracking. Placed right after the
  // try-on button (or the buy button when try-on is off).
  if (productState.sizeChartAvailable && !hasSizeChartButton()) {
    const insertAfter: Element = tryonBtn ?? anchor;
    const cfg = window.Pilcha?._config;
    const sgStyle = cfg?.sizeGuideButtonStyle;
    const sgColor = cfg?.sizeGuideButtonColor?.trim();
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.setAttribute('data-pilcha-size-chart-button', '');
    btn.setAttribute('data-product-id', productId);
    if (sgColor) {
      // Filled button mode.
      btn.style.cssText =
        'display:inline-flex;max-width:100%;align-items:center;justify-content:center;' +
        'margin:8px 0;padding:11px 18px;' +
        `border:${borderCss(sgStyle)};border-radius:${radiusCss(sgStyle)};` +
        `background:${sgColor};color:${textColorCss(sgStyle)};` +
        `font-family:${fontFamilyCss(sgStyle)};font-size:14px;line-height:1.2;` +
        fontWeightCss(sgStyle, 600) +
        typographyExtrasCss(sgStyle, 'letter-spacing:.2px;') +
        'cursor:pointer;box-sizing:border-box;';
    } else {
      // Compact text-link mode (default). A white text colour (the filled-mode
      // default) would vanish on the storefront, so treat it as "inherit" here.
      const tc = sgStyle?.textColor;
      const linkColor = tc && tc.toLowerCase() !== '#ffffff' ? tc : 'inherit';
      btn.style.cssText =
        'display:inline-flex;align-items:center;justify-content:center;' +
        'margin:6px 0 8px;padding:2px 0;border:0;background:transparent;' +
        `color:${linkColor};font-family:${fontFamilyCss(sgStyle)};font-size:13px;` +
        fontWeightCss(sgStyle, 500) +
        typographyExtrasCss(sgStyle, '') +
        'text-decoration:underline;text-underline-offset:2px;cursor:pointer;';
    }
    setButtonContent(btn, sizeChartLabel(), sgStyle);
    btn.addEventListener('click', (ev) => {
      ev.preventDefault();
      window.Pilcha?.openSizeChart(productId);
    });
    insertAfter.parentElement?.insertBefore(btn, insertAfter.nextSibling);
  }
}

const NUBESDK_WATCH_MS = 15_000;

function removeWhenNubeSdkEmbedAppears(btn: HTMLElement): void {
  if (typeof MutationObserver === 'undefined') return;
  const observer = new MutationObserver(() => {
    if (!hasNubeSdkEmbed()) return;
    btn.remove();
    observer.disconnect();
  });
  observer.observe(document.body, { childList: true, subtree: true });
  window.setTimeout(() => observer.disconnect(), NUBESDK_WATCH_MS);
}

/**
 * spec 066: apply the try-on button's visual style (extracted so the optimistic
 * loading button and the revealed button are byte-for-byte identical → the
 * spinner→label swap causes no reflow). Two profiles: Shopify/Tiendanube get a
 * branded block button mirroring the add-to-cart width; others a compact pill.
 */
function styleTryonButton(
  btn: HTMLButtonElement,
  isShopify: boolean,
  isTiendanube: boolean,
): void {
  const style = window.Pilcha?._config?.buttonStyle;
  if (isShopify || isTiendanube) {
    styleBlockButton(btn, style, buttonColor());
  } else {
    // Compact pill (non-Shopify/Tiendanube). Honors font/weight/tracking too.
    btn.style.cssText =
      'display:inline-flex;align-items:center;justify-content:center;gap:8px;' +
      'padding:10px 16px;margin:8px 8px 8px 10px;border:1px solid currentColor;' +
      `border-radius:4px;background:transparent;cursor:pointer;vertical-align:middle;font-size:inherit;` +
      `font-family:${fontFamilyCss(style)};` + fontWeightCss(style, 400) +
      typographyExtrasCss(style, '');
  }
}


let mainLoadingPromise: Promise<unknown> | null = null;

function loadMainAndCall(
  method: 'open' | 'openSizeChart' | 'return' | 'close',
  arg?: unknown,
): void {
  const cdn = window.Pilcha?._config?.cdnUrl ?? '';
  if (!mainLoadingPromise) {
    mainLoadingPromise = import(/* @vite-ignore */ `${cdn}/main.js`);
  }
  mainLoadingPromise.then(() => {
    const impl = (
      window.Pilcha as unknown as {
        _impl?: {
          open: Function;
          openSizeChart: Function;
          return: Function;
          close: Function;
        };
      }
    )._impl;
    impl?.[method]?.(arg);
  });
}

function deriveApiBaseUrl(cdnUrl: string): string {
  if (!cdnUrl) return '';
  try {
    const url = new URL(cdnUrl);
    url.hostname = url.hostname.replace(/^cdn\./, 'api.');
    return url.toString().replace(/\/$/, '');
  } catch {
    return cdnUrl;
  }
}
